import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconMaximize,
  IconMinimize,
  IconLogout2,
  IconMenu2,
  IconMessageCircle,
  IconSettings,
  IconX
} from '@tabler/icons-react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import KlaroLogo from '@/components/haccp/KlaroLogo';
import AppIcon from '@/components/haccp/AppIcon';
import { KLARO_NAV_SECTIONS } from '@/config/klaroFeatures';
import '@/App.css';

import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import EmailVerifyPage from '@/pages/EmailVerifyPage';
import InvitationAcceptPage from '@/pages/InvitationAcceptPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import OnboardingWizard from '@/pages/OnboardingWizard';
import AuthCallback from '@/pages/AuthCallback';
import HomePage from '@/pages/marketing/HomePage';
import HowItWorksPage from '@/pages/marketing/HowItWorksPage';
import PricingPage from '@/pages/marketing/PricingPage';
import PrivacyPage from '@/pages/marketing/PrivacyPage';
import TermsPage from '@/pages/marketing/TermsPage';
import Dashboard from '@/pages/Dashboard';
import OCRPage from '@/pages/OCRPage';
import IngredientsPage from '@/pages/IngredientsPage';
import SuppliersPage from '@/pages/SuppliersPage';
import RecipesPage from '@/pages/RecipesPage';
import BatchesPage from '@/pages/BatchesPage';
import MagazzinoPage from '@/pages/MagazzinoPage';
import EtichettePage from '@/pages/EtichettePage';
import ScadenzarioPage from '@/pages/ScadenzarioPage';
import RegistroPage from '@/pages/RegistroPage';
import AllergensPage from '@/pages/AllergensPage';
import SuperAdminLogin from '@/pages/superadmin/SuperAdminLogin';
import SuperAdminDashboard from '@/pages/superadmin/SuperAdminDashboard';
import OptionsPage from '@/pages/OptionsPage';
import HaccpChat from '@/components/assistant/HaccpChat';
import { ExitProtectionProvider, useExitProtection } from '@/components/haccp/ExitProtection';
import useMobileInputVisibility from '@/hooks/useMobileInputVisibility';

const roleLabel = (role) => role === 'admin' ? 'Amministratore' : role === 'operator' ? 'Operatore' : (role || 'Utente');

function navItemIsActive(location, item) {
  const path = item.route.split('?')[0];
  if (location.pathname !== path) return false;
  if (path !== '/registro') return true;
  if (item.matchAllTabs) return true;
  const params = new URLSearchParams(location.search);
  const tab = params.get('tab') || 'activity';
  return (item.activeTab || 'activity') === tab;
}

function Sidebar({ open, setOpen, collapsed, setCollapsed }) {
  const { hasFeature } = useAuth();
  const location = useLocation();

  return (
    <TooltipProvider delayDuration={150}>
      {open && <button aria-label="Chiudi menu" className="fixed inset-x-0 bottom-0 top-[60px] z-40 bg-slate-950/35 lg:hidden" onClick={() => setOpen(false)} />}
      <aside
        data-collapsed={collapsed ? 'true' : 'false'}
        className={`klaro-sidebar fixed bottom-0 left-0 top-[60px] z-50 w-[310px] max-w-[88vw] transform border-r border-[#E4E7EC] bg-white lg:relative lg:inset-auto lg:max-w-none lg:translate-x-0 ${collapsed ? 'lg:w-[82px]' : 'lg:w-[244px]'} ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex items-center justify-end border-b border-[#EEF1F5] px-2.5 py-1.5 lg:hidden">
            <button type="button" onClick={() => setOpen(false)} aria-label="Chiudi menu" className="touch-target inline-flex items-center justify-center rounded-xl text-[#65748E] hover:bg-slate-100">
              <IconX className="h-6 w-6" stroke={1.8} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="klaro-sidebar-tab hidden lg:inline-flex"
            aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}
            title={collapsed ? 'Espandi menu' : 'Comprimi menu'}
          >
            {collapsed ? <IconChevronRight className="h-4 w-4" /> : <IconChevronLeft className="h-4 w-4" />}
          </button>

          <nav className="klaro-sidebar-nav flex flex-1 flex-col justify-between overflow-y-auto overflow-x-hidden px-2.5 py-2" aria-label="Navigazione principale">
            {KLARO_NAV_SECTIONS
              .map((section) => ({ ...section, items: section.items.filter((item) => hasFeature(item.feature)) }))
              .filter((section) => section.items.length > 0)
              .map((section) => (
                <div key={section.label} className="mb-3 last:mb-0">
                  <p className="klaro-sidebar-section-label mb-1 px-3 text-[10px] font-bold tracking-[.14em] text-[#A0AAC0]">{section.label}</p>
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const active = navItemIsActive(location, item);
                      const isWideIcon = item.id === 'traceability';
                      const link = (
                        <Link
                          key={item.id}
                          to={item.route}
                          data-testid={`nav-${item.id}`}
                          onClick={() => setOpen(false)}
                          aria-label={collapsed ? item.menuLabel : undefined}
                          aria-current={active ? 'page' : undefined}
                          className={`klaro-sidebar-link flex min-h-10 items-center rounded-xl px-3 text-[14px] font-semibold ${active ? 'bg-[#ECFBF2] text-[#119B49]' : 'text-[#34425C] hover:bg-[#F7F9FC] hover:text-[#0A1737]'}`}
                        >
                          <span className="klaro-sidebar-icon-slot"><AppIcon icon={item.menuIcon} size={isWideIcon ? 'sidebarWide' : 'sidebar'} className="klaro-sidebar-icon" /></span>
                          <span className="klaro-sidebar-item-label">{item.menuLabel}</span>
                        </Link>
                      );
                      return collapsed ? (
                        <Tooltip key={item.id}><TooltipTrigger asChild>{link}</TooltipTrigger><TooltipContent side="right" className="hidden lg:block">{item.menuLabel}</TooltipContent></Tooltip>
                      ) : link;
                    })}
                  </div>
                </div>
              ))}
          </nav>
        </div>
      </aside>
    </TooltipProvider>
  );
}

function useFullscreenController() {
  const [isTouchTarget, setIsTouchTarget] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(document.fullscreenElement || document.webkitFullscreenElement));

  useEffect(() => {
    const detectTouchTarget = () => {
      const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
      setIsTouchTarget(Boolean(coarsePointer || navigator.maxTouchPoints > 0));
    };
    const syncFullscreen = () => setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
    detectTouchTarget();
    window.addEventListener('resize', detectTouchTarget);
    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);
    return () => {
      window.removeEventListener('resize', detectTouchTarget);
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
    };
  }, []);

  const toggleFullscreen = () => {
    const root = document.documentElement;
    const request = root?.requestFullscreen || root?.webkitRequestFullscreen;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    if (isFullscreen) {
      if (!exit) return toast.error('Il browser non consente di uscire dal fullscreen da Klaro.');
      try { Promise.resolve(exit.call(document)).catch(() => toast.error('Il browser non ha consentito l’uscita dal fullscreen.')); }
      catch { toast.error('Il browser non ha consentito l’uscita dal fullscreen.'); }
      return;
    }
    const requestIsPrefixed = request === root?.webkitRequestFullscreen;
    const fullscreenEnabled = requestIsPrefixed
      ? document.webkitFullscreenEnabled !== false
      : document.fullscreenEnabled !== false;
    if (!request || !fullscreenEnabled) {
      toast.error('Il fullscreen dell’app non è disponibile in questo browser.');
      return;
    }
    try { Promise.resolve(request.call(root)).catch(() => toast.error('Il browser non ha consentito l’attivazione del fullscreen.')); }
    catch { toast.error('Il browser non ha consentito l’attivazione del fullscreen.'); }
  };
  return { isTouchTarget, isFullscreen, toggleFullscreen };
}

function TopHeader({ onOpenMenu, onOpenChat, showChatButton = false }) {
  const { user, logout } = useAuth();
  const { requestExit } = useExitProtection();
  const fullscreen = useFullscreenController();
  return (
    <header className="klaro-top-header">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <button type="button" onClick={onOpenMenu} className="touch-target inline-flex shrink-0 items-center justify-center rounded-xl text-[#0A1737] hover:bg-slate-100 lg:hidden" data-testid="mobile-menu-btn" aria-label="Apri menu">
          <IconMenu2 className="h-6 w-6" stroke={1.8} />
        </button>
        <KlaroLogo header className="klaro-header-logo" />
      </div>

      <div className="klaro-header-actions">
        {showChatButton && (
          <button
            type="button"
            onClick={onOpenChat}
            className="klaro-header-chat-button"
            aria-label="Apri Chat HACCP AI"
            title="Chat HACCP AI"
            data-testid="header-chat-launcher"
          >
            <IconMessageCircle className="h-6 w-6" stroke={1.8} />
            <span className="klaro-header-chat-status" aria-hidden="true" />
          </button>
        )}

        <FullscreenControl controller={fullscreen} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="flex min-h-12 items-center gap-2 rounded-xl px-1.5 text-left transition-colors hover:bg-slate-50 sm:gap-3 sm:px-2" aria-label="Apri profilo">
              {user?.picture ? (
                <img src={user.picture} alt="Profilo utente" className="h-10 w-10 rounded-full border border-[#DDE4EE] object-cover sm:h-11 sm:w-11" />
              ) : (
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#07506D] font-semibold text-white sm:h-11 sm:w-11">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
              )}
              <span className="hidden min-w-0 md:block">
                <span className="block max-w-[170px] truncate text-sm font-semibold text-[#0A1737]">{user?.name || 'Operatore'}</span>
                <span className="block text-xs text-[#7B8AA7]">{roleLabel(user?.role)}</span>
              </span>
              <IconChevronDown className="h-4 w-4 text-[#34425C]" stroke={2} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <FullscreenControl controller={fullscreen} compact />
            <div className="px-2 py-1.5 md:hidden"><p className="truncate text-sm font-semibold">{user?.name || 'Operatore'}</p><p className="text-xs text-muted-foreground">{roleLabel(user?.role)}</p></div>
            <DropdownMenuItem asChild><Link to="/opzioni"><IconSettings className="h-4 w-4" />Impostazioni</Link></DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => requestExit(logout)} className="text-red-600 focus:text-red-700"><IconLogout2 className="h-4 w-4" />Esci</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function FullscreenControl({ controller, compact = false }) {
  const { isTouchTarget, isFullscreen, toggleFullscreen } = controller;
  if (!isTouchTarget) return null;
  const icon = isFullscreen ? <IconMinimize className="h-5 w-5" stroke={1.8} /> : <IconMaximize className="h-5 w-5" stroke={1.8} />;
  const label = isFullscreen ? 'Esci dal fullscreen' : 'Attiva fullscreen';
  if (compact) return <DropdownMenuItem className="min-[400px]:hidden" onSelect={toggleFullscreen}>{icon}{label}</DropdownMenuItem>;
  return (
    <button
      type="button"
      onClick={toggleFullscreen}
      className="touch-target hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[#0A1737] hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary min-[400px]:inline-flex"
      aria-label={label}
      title={label}
      data-testid="header-fullscreen-toggle"
    >
      {icon}
    </button>
  );
}

function MainLayout({ children }) {
  const { hasFeature } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('haccp-sidebar-collapsed') === '1');
  const [chatOpenRequest, setChatOpenRequest] = useState(0);

  useEffect(() => {
    const syncSidebarPreference = (event) => {
      if (typeof event?.detail?.collapsed === 'boolean') setSidebarCollapsed(event.detail.collapsed);
    };
    window.addEventListener('haccp-sidebar-preference', syncSidebarPreference);
    return () => window.removeEventListener('haccp-sidebar-preference', syncSidebarPreference);
  }, []);

  const updateCollapsed = (value) => {
    setSidebarCollapsed(value);
    localStorage.setItem('haccp-sidebar-collapsed', value ? '1' : '0');
  };

  return (
    <div className="flex h-[100dvh] flex-col bg-background">
      <TopHeader
        onOpenMenu={() => setSidebarOpen(true)}
        onOpenChat={() => setChatOpenRequest((value) => value + 1)}
        showChatButton={hasFeature('ai.chat')}
      />
      <div className="flex min-h-0 flex-1">
        <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} collapsed={sidebarCollapsed} setCollapsed={updateCollapsed} />
        <main className="klaro-app-main min-h-0 min-w-0 flex-1 overflow-y-auto bg-background">{children}</main>
      </div>
      {hasFeature('ai.chat') && <HaccpChat openRequest={chatOpenRequest} />}
    </div>
  );
}

function ProtectedRoute({ children, feature = null }) {
  const { user, loading, hasFeature, licenseStatus, logout } = useAuth();
  const { requestExit } = useExitProtection();
  const location = useLocation();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background"><div className="text-center"><span className="mx-auto block h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" /><p className="mt-4 text-[#667085]">Caricamento...</p></div></div>;
  if (!user && !location.state?.user) return <Navigate to="/login" replace />;
  if (licenseStatus && !['active', 'trial'].includes(licenseStatus)) {
    const title = licenseStatus === 'suspended' ? 'Account sospeso' : 'Licenza non attiva';
    const description = licenseStatus === 'expired' ? 'La licenza aziendale è scaduta.' : 'Contatta l’amministratore Klaro per verificare lo stato della licenza.';
    return <div className="flex min-h-screen items-center justify-center bg-background p-5"><div className="surface max-w-md p-6 text-center"><h1 className="font-heading text-2xl font-semibold text-[#172033]">{title}</h1><p className="mt-2 text-sm text-[#667085]">{description}</p><Button onClick={() => requestExit(logout)} variant="outline" className="mt-5">Esci</Button></div></div>;
  }
  if (feature && !hasFeature(feature)) {
    return <MainLayout><div className="app-page"><div className="app-page-inner"><div className="surface mx-auto max-w-xl p-6 text-center"><h1 className="font-heading text-2xl font-semibold text-[#172033]">Funzione non inclusa nel piano</h1><p className="mt-2 text-sm text-[#667085]">Questa funzione non è disponibile per il piano attivo della tua azienda.</p><Button asChild className="mt-5"><Link to="/dashboard">Torna alla Dashboard</Link></Button></div></div></div></MainLayout>;
  }
  return <MainLayout>{children}</MainLayout>;
}

function AppRouter() {
  const location = useLocation();
  useMobileInputVisibility(location.pathname === '/carica-merce');
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <ExitProtectionProvider><Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<EmailVerifyPage />} />
      <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/onboarding" element={<OnboardingWizard />} />
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />
      <Route path="/superadmin" element={<SuperAdminDashboard />} />
      <Route path="/dashboard" element={<ProtectedRoute feature="dashboard.view"><Dashboard /></ProtectedRoute>} />
      <Route path="/carica-merce" element={<ProtectedRoute feature="inventory.receive"><OCRPage /></ProtectedRoute>} />
      <Route path="/ricette" element={<ProtectedRoute feature="recipes.manage"><RecipesPage /></ProtectedRoute>} />
      <Route path="/batch" element={<ProtectedRoute feature="production.manage"><BatchesPage /></ProtectedRoute>} />
      <Route path="/magazzino" element={<ProtectedRoute feature="inventory.manage"><MagazzinoPage /></ProtectedRoute>} />
      <Route path="/etichette" element={<ProtectedRoute feature="labels.print"><EtichettePage /></ProtectedRoute>} />
      <Route path="/etichette/stampa" element={<Navigate to="/etichette" replace />} />
      <Route path="/scadenzario" element={<ProtectedRoute feature="expiry.manage"><ScadenzarioPage /></ProtectedRoute>} />
      <Route path="/opzioni" element={<ProtectedRoute><OptionsPage /></ProtectedRoute>} />
      <Route path="/fornitori" element={<ProtectedRoute feature="catalog.manage"><SuppliersPage /></ProtectedRoute>} />
      <Route path="/ingredienti" element={<ProtectedRoute feature="catalog.manage"><IngredientsPage /></ProtectedRoute>} />
      <Route path="/allergeni" element={<ProtectedRoute feature="catalog.manage"><AllergensPage /></ProtectedRoute>} />
      <Route path="/registro" element={<ProtectedRoute feature="haccp.register"><RegistroPage /></ProtectedRoute>} />
      <Route path="/ocr" element={<Navigate to="/carica-merce" replace />} />
      <Route path="/lots" element={<Navigate to="/magazzino" replace />} />
      <Route path="/labels" element={<Navigate to="/etichette" replace />} />
      <Route path="/ingredients" element={<Navigate to="/ingredienti" replace />} />
      <Route path="/suppliers" element={<Navigate to="/fornitori" replace />} />
      <Route path="/recipes" element={<Navigate to="/ricette" replace />} />
      <Route path="/batches" element={<Navigate to="/batch" replace />} />
      <Route path="/come-funziona" element={<HowItWorksPage />} />
      <Route path="/prezzi" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/termini" element={<TermsPage />} />
      <Route path="/" element={<HomePage />} />
    </Routes></ExitProtectionProvider>
  );
}

const router = createBrowserRouter([{ path: '*', element: <AppRouter /> }]);

export default function App() {
  return <AuthProvider><RouterProvider router={router} /><Toaster position="bottom-right" /></AuthProvider>;
}
